import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  RefreshCcw,
  Search,
  Settings2,
  UserPlus,
  Users,
  ShieldCheck,
  User,
  ChevronRight,
  Filter
} from "lucide-react";

/**
 * Permisos (Modelo Productos)
 * Estructura:
 * 1) Header: Título + Acciones
 * 2) KPIs
 * 3) Filtros
 * 4) Tabla/Listado
 *
 * Mantiene:
 * - fetchDatos()
 * - onAddUser, onConfigRoles, onEditUser
 */
function Permisos({ onAddUser, onConfigRoles, onEditUser, onNavigate }) {
  const { profile } = useAuth();
  const canManageUsers = ["admin", "administrador"].includes(String(profile?.role || "").toLowerCase());

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtros estilo Productos
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("todos"); // todos | admin | cajero | otros

  useEffect(() => {
    if (profile?.organization_id) fetchDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const fetchDatos = async () => {
    setLoading(true);
    try {
      // 1. users list from profiles
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("organization_id", profile.organization_id)
        .order("full_name", { ascending: true });

      if (error) throw error;
      setUsuarios(data || []);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  // KPIs calculados
  const kpis = useMemo(() => {
    const total = usuarios.length;
    const admins = usuarios.filter((u) => u.role === "admin" || u.role === "administrador").length;
    const cajeros = usuarios.filter((u) => u.role === "cajero").length;
    return { total, admins, cajeros };
  }, [usuarios]);

  // Filtrado
  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter((u) => {
      // texto
      const txt = busqueda.toLowerCase();
      const matchTxt =
        (u.full_name || "").toLowerCase().includes(txt) ||
        (u.email || "").toLowerCase().includes(txt) ||
        (u.role || "").toLowerCase().includes(txt);

      // rol
      let matchRol = true;
      if (filtroRol === "admin") {
        matchRol = u.role === "admin" || u.role === "administrador";
      } else if (filtroRol === "cajero") {
        matchRol = u.role === "cajero";
      } else if (filtroRol === "otros") {
        matchRol = !["admin", "administrador", "cajero"].includes(u.role);
      }

      return matchTxt && matchRol;
    });
  }, [usuarios, busqueda, filtroRol]);

  return (
    // CONTENEDOR NOVA STANDARD
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER TIPO SISTEMA */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ShieldCheck size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Control de Acceso</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Gestión de usuarios y permisos del sistema.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          {/* BOTONES DE ACCIÓN */}
          <button
            onClick={() => onNavigate ? onNavigate("roles-config") : onConfigRoles && onConfigRoles()}
            disabled={!canManageUsers}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Settings2 size={16} /> Config. Roles
          </button>

          <button
            onClick={() => onNavigate ? onNavigate("usuario-nuevo") : onAddUser && onAddUser()}
            disabled={!canManageUsers}
            className="px-5 py-2.5 rounded-2xl bg-[#3ff2aa] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 border border-[#3ff2aa]"
          >
            <UserPlus size={16} strokeWidth={3} /> Nuevo Usuario
          </button>
        </div>
      </div>

      {!canManageUsers ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] font-bold text-amber-800">
          Tenés acceso de consulta. Solo un administrador de esta organización puede crear usuarios o editar roles y módulos.
        </div>
      ) : null}

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Usuarios</p>
            <Users size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Registrados</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Administradores</p>
            <ShieldCheck size={16} className="text-purple-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.admins}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Acceso total</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operadores</p>
            <User size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.cajeros}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Punto de venta</p>
        </div>
      </div>

      {/* FILTROS / BÚSQUEDA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4">
         <div className="flex flex-col md:flex-row items-center gap-3">
             <div className="relative flex-1 w-full">
                 <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                    <Search size={10} /> Buscar Usuario
                 </label>
                 <div className="relative">
                    <input 
                        type="text"
                        placeholder="NOMBRE, EMAIL O ROL..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase text-slate-700"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                 </div>
             </div>

             <div className="w-full md:w-48">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                    <Filter size={10} /> Filtrar Rol
                </label>
                <select
                  value={filtroRol}
                  onChange={(e) => setFiltroRol(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-2xl text-[10px] font-bold outline-none cursor-pointer uppercase text-slate-700 h-[38px]"
                >
                  <option value="todos">Todos los roles</option>
                  <option value="admin">Administradores</option>
                  <option value="cajero">Cajeros</option>
                  <option value="otros">Otros</option>
                </select>
             </div>

             <div className="flex items-end">
                <button
                  onClick={fetchDatos}
                  className="bg-white border border-slate-200 hover:border-slate-300 text-slate-700 p-2.5 rounded-2xl shadow-sm transition-all h-[38px] w-[38px] flex items-center justify-center mt-auto"
                  title="Refrescar lista"
                >
                  <RefreshCcw size={16} className={loading ? "animate-spin" : ""} />
                </button>
             </div>
         </div>
      </div>

      {/* TABLA DE USUARIOS - ESTILO NOVA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full min-h-[400px]">
        {/* Header Tabla */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Users size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Directorio de Usuarios
                </span>
            </div>
            <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">
                {usuariosFiltrados.length} Registros
            </span>
        </div>

        <div className="overflow-x-auto imp-table-wrap">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-5 py-3 pl-5">Usuario</th>
                <th className="px-4 py-3">Contacto</th>
                <th className="px-4 py-3">Rol Asignado</th>
                <th className="px-4 py-3 text-right pr-5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-400">
                        <RefreshCcw size={24} className="animate-spin mb-2" />
                        <p className="text-[10px] font-bold uppercase tracking-widest">Cargando usuarios...</p>
                    </div>
                  </td>
                </tr>
              ) : (
                usuariosFiltrados.map((u) => {
                  const roleLower = (u.role || "").toLowerCase();
                  let roleBadge = "bg-slate-100 text-slate-600 border-slate-200";
                  if (roleLower.includes("admin")) roleBadge = "bg-purple-50 text-purple-600 border-purple-100";
                  else if (roleLower.includes("cajero")) roleBadge = "bg-blue-50 text-blue-600 border-blue-100";

                  return (
                    <tr
                      key={u.id}
                      onClick={() => onNavigate ? onNavigate("usuario-detalle", u.id) : onEditUser && onEditUser(u)}
                      className="hover:bg-blue-50/20 transition-all cursor-pointer group"
                    >
                      <td className="px-5 py-3 pl-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm shrink-0">
                            <User size={14} />
                          </div>
                          <div>
                            <p className="text-[11px] font-black text-slate-700 uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors">
                              {u.full_name || "Sin Nombre"}
                            </p>
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                                ID: {u.id.slice(0, 6)}...
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span className="text-[10px] font-bold text-slate-600 lowercase tracking-wide block">
                          {u.email || "—"}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${roleBadge}`}
                        >
                          {u.role || "usuario"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-right pr-5">
                        <ChevronRight
                          size={16}
                          className="text-slate-300 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all inline-block"
                        />
                      </td>
                    </tr>
                  );
                })
              )}

              {usuariosFiltrados.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="px-6 py-16 text-center">
                    <Users className="text-slate-200 mx-auto mb-4" size={44} strokeWidth={1} />
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic">
                      No se encontraron usuarios con los filtros aplicados.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="h-10" />
    </div>
  );
}

export default Permisos;
